/**
 * @module actions/Volume
 */
import isNumber from 'lodash/isNumber';
import { type Dispatch } from 'redux';
import { type PayloadAction, createAction } from 'typesafe-actions';

import { VolumeActions } from '../constants/Volume';
import { getGlobalVolume } from '../lib/VolumeUtils';

interface SetVolumePayload {
  volume: number;
}

export type SetVolumeAction = PayloadAction<typeof VolumeActions.SET_VOLUME, SetVolumePayload>;
/**
 * Sets the current global volume value. This is a generic value on a 100 point
 * scale. It is interpreted differently but shared by audio and video contexts.
 * It is also persisted and restored in local storage. The value must be between
 * 0 - 100 inclusive and a number. If not a number, the current volume value
 * in the store will be used. The volume applied will be clamped to the scale
 * and rounded to the nearest whole number.
 *
 * @param {number} value The value to set the volume to
 * @returns {Function} The action-thunk
 */
export const setGlobalVolume =
  (value: number) =>
  (dispatch: Dispatch<SetVolumeAction>, getState: $TSFixMeFunction): SetVolumeAction => {
    let volume = value;
    if (!isNumber(volume)) volume = getGlobalVolume(getState());
    volume = Math.round(Math.max(Math.min(100, volume), 0));
    return dispatch({
      type: VolumeActions.SET_VOLUME,
      payload: {
        volume,
      },
    });
  };

interface SetMutedPayload {
  muted: boolean;
}

/**
 * Sets the current global muted value.
 * @param {Boolean} mute true|false to set the current muted value
 */
export const setGlobalMuted = createAction(VolumeActions.SET_MUTED, (mute = true) => ({
  muted: !!mute,
}))<SetMutedPayload>();
export type SetMutedAction = ReturnType<typeof setGlobalMuted>;
